import React, { useEffect, useState } from "react";
import axios from "axios";
import { getUserId, getUserEmail } from "../utils/userUtils";
import { getSubscriptionLimits } from "../utils/mediaUsageUtils";
import { getUpgradePath } from "../utils/subscriptionUtils";

const MySubscriptions = ({ user }) => {
  const [subscription, setSubscription] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const userId = getUserId(user);
        if (!userId) {
          console.error("No user ID available");
          return;
        }

        const response = await axios.get(
          `https://d4gscm5o97.execute-api.us-east-1.amazonaws.com/prod/subscriptions/${userId}`
        );
        setSubscription(response.data);
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        // Set default free subscription on error
        setSubscription({
          subscription_tier: 'free',
          subscription_status: 'free',
          plan_name: 'Free Plan'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  if (loading) {
    return (
      <div className="text-center text-gray-400">Loading subscriptions...</div>
    );
  }

  const userEmail = getUserEmail(user);
  const tier = subscription.subscription_tier || 'free';
  const limits = getSubscriptionLimits(tier);
  const upgradePath = getUpgradePath(tier);

  const getPlanColor = (tier) => {
    switch (tier) {
      case 'free': return 'text-gray-400';
      case '0': return 'text-blue-400';
      case '1': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'free': return '🆓';
      case '0': return '📦';
      case '1': return '💎';
      default: return '❓';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-r from-gray-800 via-gray-900 to-black shadow-lg rounded-lg text-white">
      <h2 className="text-2xl font-semibold mb-6">Your Subscription</h2>
      
      {/* Current Plan */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getPlanIcon(tier)}</span>
            <div>
              <h3 className={`text-xl font-semibold ${getPlanColor(tier)}`}>
                {limits.name}
              </h3>
              <p className="text-gray-400 text-sm">
                {subscription.subscription_status === 'free' ? 'Free account' : 
                 subscription.subscription_status === 'active' ? 'Active subscription' :
                 subscription.subscription_status}
              </p>
            </div>
          </div>
        </div>

        {/* Plan Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-700 rounded p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Pictures per month</span>
              <span className="font-semibold">{limits.monthly_pictures}</span>
            </div>
          </div>
          <div className="bg-gray-700 rounded p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Videos per month</span>
              <span className="font-semibold">{limits.monthly_videos}</span>
            </div>
          </div>
        </div>

        {/* Upgrade Section */}
        {upgradePath.nextTier && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 mt-4">
            <h4 className="font-semibold mb-2">Want more?</h4>
            <p className="text-sm mb-3">{upgradePath.benefits}</p>
            <a
              href={upgradePath.upgradeUrl}
              className="inline-block bg-white text-gray-900 px-4 py-2 rounded font-medium hover:bg-gray-100 transition duration-300"
            >
              Upgrade to {upgradePath.nextTier}
            </a>
          </div>
        )}
      </div>

      {/* Manage Subscription */}
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-4">
          Manage Your Subscription
        </h2>
        <a
          href={
            "https://billing.stripe.com/p/login/test_eVa3fO2nM3EGdWw7ss/?prefilled_email=" +
            userEmail
          }
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition duration-300"
        >
          Manage on Stripe
        </a>
      </div>
    </div>
  );
};

export default MySubscriptions;
